import React from 'react';
import PropTypes from 'prop-types';
import styled, { css } from 'styled-components';
import BigNumber from 'bignumber.js';
import dateFormat from '../../utils/dateFormat';
import Pagination from './Pagination';
import EmptyState from './EmptyState';
import bsvIcon from './assets/bsv-icon.svg';

const StyledTable = styled.table`
  width: 100%;
`;

const StyledTbody = styled.tbody`
  &:before {
    content: '-';
    display: block;
    line-height: 1px;
    color: transparent;
  }
`;

const StyledTr = styled.tr`
  background-color: ${({ transparent }) => (transparent ? 'transparent' : '#1f0d53')};
  width: 100%;
  box-shadow: ${({ selected }) => (selected ? 'inset 0 0 60px 0 rgba(255, 41, 118, 0.7)' : '')};
  border-bottom: ${({ borderBottom }) => (borderBottom ? '1px solid rgba(199, 186, 241, 0.2)' : '4px solid #12033d')};

}
`;

const StyledTh = styled.th`
  padding: 8px 0 8px 24px;
  white-space: nowrap;
  color: #f0ecff;
  text-align: left;

  &:first-child {
    border-top-left-radius: 4px;
    border-bottom-left-radius: 4px;
  }

  &:last-child {
    border-bottom-right-radius: 4px;
    border-top-right-radius: 4px;
    padding: 12px 24px 12px 24px;
    text-align: end;
  }
  &:nth-child(3) {
    text-align: ${({ textAlign }) => textAlign};
  }
  ${({ transactions }) => transactions
    ? css`
          &:nth-child(3) {
            width: 120px;
            display: inline-block;
            overflow: hidden;
            text-overflow: ellipsis;
            white-space: nowrap;
          }
        `
    : ''}
  img {
    width: 14px;
    margin-left: 4px;
  }
  span {
    opacity: 0.6;
  }
`;

const StyledTd = styled.td`
  padding: 12px 0 12px 24px;
  text-align: left;
  color: ${({ color, theme }) => (color ? '#ffbd3e' : theme.color.white)};
  &:first-child {
    border-top-left-radius: 4px;
    border-bottom-left-radius: 4px;
  }
  &:last-child {
    border-bottom-right-radius: 4px;
    border-top-right-radius: 4px;
    padding: 12px 24px 12px 24px;
    text-align: end;
  }
  &:nth-child(3) {
    text-align: ${({ textAlign }) => textAlign};
  }

  ${({ transactions }) => transactions
    ? css`
          &:nth-child(3) {
            width: 120px;
            display: inline-block;
            overflow: hidden;
            text-overflow: ellipsis;
            white-space: nowrap;
          }
        `
    : ''}
`;

const StyledTableContainer = styled.div`
  overflow-x: auto;
`;

const Table = ({
  headerTitles,
  bodyValues,
  rowKey,
  prevPage,
  changePage,
  nextPage,
  pagination,
  transactions,
  withPagination,
  textAlign,
}) => (
  <>
    <StyledTableContainer>
      <StyledTable>
        <thead>
          <StyledTr transparent borderBottom className="small">
            {Object.keys(headerTitles).map(title => (
              /* eslint-disable */
              <StyledTh textAlign={textAlign} key={title}>
                {headerTitles[title]}
                {headerTitles[title] === headerTitles.wagered ||
                headerTitles[title] === headerTitles.commissions ||
                headerTitles[title] === headerTitles.commission ||
                headerTitles[title] === headerTitles.amount ? (
                  <img src={bsvIcon} alt="icon" />
                ) : null}
              </StyledTh>
              /* eslint-enable */
            ))}
          </StyledTr>
        </thead>
        {bodyValues.length ? (
          <StyledTbody>
            {transactions
              ? bodyValues.map(item => (
                <StyledTr key={item[rowKey]}>
                  {Object.keys(headerTitles).map(key => {
                    if (key === 'created_at') {
                      return (
                        <StyledTd key={key} fontSize="14px" lineHeight="1.57">
                          {dateFormat(item[key])}
                        </StyledTd>
                      );
                    }
                    if (key === 'game_name') {
                      return <StyledTd key={key}>{item[key].charAt(0).toUpperCase() + item[key].slice(1)}</StyledTd>;
                    }
                    return (
                      <StyledTd textAlign={textAlign} key={key} transactions={transactions && key === rowKey}>
                        {typeof item[key] === 'number' ? new BigNumber(item[key]).toFixed() : item[key]}
                      </StyledTd>
                    );
                  })}
                </StyledTr>
              ))
              : bodyValues.map(item => (
                <StyledTr key={item[rowKey]}>
                  {Object.keys(headerTitles).map(key => key === 'created_at' ? (
                    <StyledTd key={key} lineHeight={item[key] === rowKey}>
                      {dateFormat(item[key])}
                    </StyledTd>
                  ) : (
                    <StyledTd
                      textAlign={textAlign}
                      key={key}
                      fontSize={key === rowKey ? '14px' : '16px'}
                      lineHeight={item[key] === rowKey}
                    >
                      {typeof item[key] === 'number' ? new BigNumber(item[key]).toFixed() : item[key]}
                    </StyledTd>
                  ))}
                </StyledTr>
              ))}
          </StyledTbody>
        ) : null}
      </StyledTable>
    </StyledTableContainer>
    {withPagination ? (
      <Pagination prevPage={prevPage} pagination={pagination} nextPage={nextPage} changePage={changePage} />
    ) : null}
    {bodyValues.length ? null : <EmptyState />}
  </>
);

Table.propTypes = {
  headerTitles: PropTypes.objectOf(PropTypes.any).isRequired,
  bodyValues: PropTypes.arrayOf(PropTypes.any).isRequired,
  rowKey: PropTypes.string.isRequired,
  prevPage: PropTypes.func,
  nextPage: PropTypes.func,
  changePage: PropTypes.func,
  pagination: PropTypes.arrayOf(PropTypes.any),
  withPagination: PropTypes.bool,
  transactions: PropTypes.bool,
  textAlign: PropTypes.string,
};

Table.defaultProps = {
  pagination: [],
  nextPage: null,
  changePage: null,
  prevPage: null,
  transactions: false,
  withPagination: false,
  textAlign: '',
};

export default React.memo(Table);
